
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Box, Button, MenuItem, Modal, useTheme } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';

import { HotelCheckOptionDTO } from '@bandido/shared/dtos';
import { ControlledSelect } from '@frontend/features/common/components/ControlledSelect';
import { HotelCheckOptionType } from '@bandido/shared/types';
import { useForm } from 'react-hook-form';
import { StandardSmallModalBox } from '@frontend/features/common/utils/modals';


const ageOptions = Array.from({ length: 21 - 16 + 1 }, (_, i) => i + 16);
const hoursOptions = Array.from({ length: 23 - 0 + 1 }, (_, i) => i + 0);

const useValidationSchema = () => {
  const { t } = useTranslation();
  return yup.object().shape({
    minAge: yup
      .number()
      .default(18)
      .typeError(t('hotels.contentManager.detail.checkOptions.editor.minAge.error'))
      .required(t('hotels.contentManager.detail.checkOptions.editor.minAge.required')),
    startTime: yup
      .number()
      .default(0)
      .typeError(t('hotels.contentManager.detail.checkOptions.editor.startTime.error'))
      .required(t('hotels.contentManager.detail.checkOptions.editor.startTime.required')),
    endTime: yup
      .number()
      .default(0)
      .typeError(t('hotels.contentManager.detail.checkOptions.editor.endTime.error'))
      .required(t('hotels.contentManager.detail.checkOptions.editor.endTime.required')),
  });
};


interface Props {
  checkOption: HotelCheckOptionDTO;
  onClose: () => void;
  onSave: (checkOption: HotelCheckOptionDTO) => void;
  open?: boolean;
}

export const CheckOptionsEditor: FC<Props> = ({ checkOption, open, onClose, onSave }) => {
  const { t } = useTranslation();
  const { type, startTime, endTime, minAge } = checkOption;
  const validationSchema = useValidationSchema();
  const theme = useTheme();

  const {
    watch,
    handleSubmit,
    control,
    formState: { isValid, errors },
  } = useForm({
    defaultValues: {
      endTime: endTime || 0,
      startTime: startTime || 0,
      minAge: minAge || 18
    },
    resolver: yupResolver(validationSchema),
  });

  const updatedStartTime = watch('startTime');
    
  const onSubmit = (data: { 
    minAge: number;
    startTime: number;
    endTime: number;
  }) => {
    const { minAge, startTime, endTime } = data;
    onSave({
      ...checkOption,
      minAge,
      startTime,
      endTime,
    });
  };


  return (
    <Modal
      open={!!open}
      onClose={onClose}
      aria-labelledby={
        type.toString() === HotelCheckOptionType.CHECK_IN.toString() ?
          t('hotels.contentManager.detail.checkOptions.editor.type.checkIn') :
          t('hotels.contentManager.detail.checkOptions.editor.type.checkOut')
      }
    >
      <StandardSmallModalBox>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)} 
          display="flex"
          flexDirection="column"
          gap={theme.spacing(2)}
          mt={theme.spacing(2)}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={theme.spacing(2)}
          >
            <ControlledSelect
              required
              control={control}
              errorMessage={errors.minAge?.message ? errors.minAge?.message.toString() : undefined}
              defaultValue={minAge}
              name="minAge"
              size="small"
              variant="outlined"
              label={t('hotels.contentManager.detail.checkOptions.editor.minAge.title')}
              placeholder={t('hotels.contentManager.detail.checkOptions.editor.minAge.title')}
              sx={{
                width: 120
              }}
            >
              {ageOptions.map((age) => (
                <MenuItem key={age} value={age}>
                  {t('hotels.contentManager.detail.checkOptions.editor.minAge.value', { value: age })}
                </MenuItem>
              ))}
            </ControlledSelect>

            <ControlledSelect
              required
              control={control}
              errorMessage={errors.startTime?.message ? errors.startTime?.message.toString() : undefined}
              defaultValue={startTime}
              name="startTime"
              size="small"
              variant="outlined"
              label={t('hotels.contentManager.detail.checkOptions.editor.startTime.title')}
              placeholder={t('hotels.contentManager.detail.checkOptions.editor.startTime.title')}
              sx={{
                width: 120
              }}
            >
              {hoursOptions.filter((_, idx) => idx < hoursOptions.length -1).map((hour) => (
                <MenuItem key={hour} value={hour} sx={{ textAlign: 'right' }}>
                  {t('hotels.contentManager.detail.checkOptions.editor.startTime.value', { value: hour })}
                </MenuItem>
              ))}
            </ControlledSelect>

            <ControlledSelect
              required
              control={control}
              errorMessage={errors.endTime?.message ? errors.endTime.message.toString() : undefined}
              defaultValue={endTime}
              name="endTime"
              size="small"
              variant="outlined"
              label={t('hotels.contentManager.detail.checkOptions.editor.endTime.title')}
              placeholder={t('hotels.contentManager.detail.checkOptions.editor.endTime.title')}
              sx={{
                width: 100
              }}
            >
              {hoursOptions.filter((hour) => hour > updatedStartTime).map((hour) => (
                <MenuItem key={hour} value={hour}>
                  {t('hotels.contentManager.detail.checkOptions.editor.endTime.value', { value: hour })}
                </MenuItem>
              ))}
            </ControlledSelect>
          </Box>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            size='small'
            fullWidth
            disabled={!isValid}
          >
            {t('common.form.save')}
          </Button>
        </Box>
      </StandardSmallModalBox>
    </Modal>
  );
};

